/**
 * «Все референсы»: каждый референс всех библиотек одним полотном.
 *
 * Фильтры серверные и живут в адресе (`?category=&library=&tag=&verdict=&q=&sort=`):
 * сервер отдаёт не больше `max` строк и без страниц, поэтому сузить выборку
 * можно только запросом, а ссылку на срез можно переслать. Сортировка — на
 * клиенте, по уже пришедшему ответу. Сменился адрес (фильтр, поиск, порядок) —
 * сетка снова с первой порции (`resetKey`).
 *
 * «Поиск» (по описанию, фото, платью) — `PhotoSearchPanel` с выбором библиотеки: есть выдача — вместо
 * полотна её плитка, серверные фильтры экрана к ней не относятся.
 */
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, ScanSearch } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { allRefsQuery, parseAllRefs, type Sort, type VerdictParam } from "@/features/library/model/allRefsParams";
import { LibNav } from "@/components/library/LibNav";
import { PhotoSearchPanel, type SimilarFound } from "@/components/library/PhotoSearchPanel";
import { ReferenceBrowser } from "@/components/library/ReferenceBrowser";
import { TagManager } from "@/components/trends/TagManager";
import { btnGhost, btnPrimary, Chip, ErrorLine, input, Loading, Segmented } from "@/components/trends/ui";
import {
  libraryKeys,
  loadAllItems,
  loadLibraries,
  type AllItemsParams,
  type ReferenceItem,
} from "@/lib/library";
import { num } from "@/lib/media";
import { scoreMap, showsPercent } from "@/lib/similar";
import { loadTags, tagKeys } from "@/lib/tags";
import { retryQuery } from "@/lib/trends";

const SEARCH_DELAY_MS = 300;

function sorted(items: ReferenceItem[], sort: Sort): ReferenceItem[] {
  if (sort === "old") return [...items].reverse();
  if (sort === "library")
    return [...items].sort((a, b) => a.library_name.localeCompare(b.library_name, "ru") || a.id - b.id);
  return items;
}

export function AllReferencesPage() {
  const [search, setSearch] = useSearchParams();
  const { category, libraryId, tag, verdict, q, sort } = parseAllRefs(search);
  // Ссылка с `?similar_dress=` / `?similar_text=` открывает экран сразу с поиском.
  const [searchOpen, setSearchOpen] = useState(() => search.has("similar_dress") || search.has("similar_text"));
  const [found, setFound] = useState<SimilarFound | null>(null);
  const [managing, setManaging] = useState(false);
  const queryClient = useQueryClient();
  const scores = useMemo(() => (found ? scoreMap(found.view.items) : null), [found]);

  /** Пустые значения из адреса удаляются; остальные параметры не трогаем. */
  const patch = useCallback(
    (changes: Record<string, string | null>) =>
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(changes)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      ),
    [setSearch],
  );

  // Поле поиска и адрес: поле пишет в адрес с задержкой, а адрес, изменённый
  // снаружи (ссылка в LibNav, «назад»), возвращается в поле. `sentQ` отличает
  // наш собственный отложенный пересказ q от чужого, иначе набор на ходу
  // откатывался бы к уже записанному.
  const [qText, setQText] = useState(q);
  const sentQ = useRef(q);
  const writeQ = useCallback(
    (text: string) => {
      sentQ.current = text.trim();
      patch({ q: text.trim() || null });
    },
    [patch],
  );
  useEffect(() => {
    if (q === sentQ.current) return;
    sentQ.current = q;
    setQText(q);
  }, [q]);
  useEffect(() => {
    if (qText.trim() === q) return;
    const timer = setTimeout(() => writeQ(qText), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [qText, q, writeQ]);

  const params: AllItemsParams = allRefsQuery({ category, libraryId, tag, verdict, q });

  const libs = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries, retry: retryQuery, staleTime: 30_000 });
  const tagsQuery = useQuery({ queryKey: tagKeys.list, queryFn: loadTags, retry: retryQuery });
  const data = useQuery({
    queryKey: libraryKeys.all(params),
    queryFn: () => loadAllItems(params),
    retry: retryQuery,
    placeholderData: keepPreviousData,
  });

  const tags = tagsQuery.data?.tags;
  // Тег или библиотеку удалили, а ссылка осталась: срез был бы пуст. Решаем
  // только по свежему словарю — кэш мог быть старше ссылки.
  const tagsFresh = tagsQuery.isSuccess && !tagsQuery.isFetching;
  const libsFresh = libs.isSuccess && !libs.isFetching;
  useEffect(() => {
    if (tagsFresh && typeof tag === "number" && !tags?.some((t) => t.id === tag)) patch({ tag: null });
  }, [tag, tags, tagsFresh, patch]);
  const libraryIds = libs.data?.groups.flatMap((g) => g.libs.map((l) => l.id));
  useEffect(() => {
    if (libsFresh && libraryId !== null && !libraryIds?.includes(libraryId)) patch({ library: null });
  }, [libraryId, libraryIds, libsFresh, patch]);

  const items = useMemo(() => sorted(data.data?.items ?? [], sort), [data.data, sort]);

  const groups = libs.data?.groups ?? [];
  const shownGroups = category ? groups.filter((g) => g.code === category) : groups;

  const pickCategory = (code: string) => {
    const keep =
      libraryId !== null && (!code || groups.some((g) => g.code === code && g.libs.some((l) => l.id === libraryId)));
    patch({ category: code || null, ...(keep ? {} : { library: null }) });
  };

  const total = data.data?.total ?? 0;
  const filtered = Boolean(category || libraryId || tag !== null || verdict || q);

  return (
    <div className="grid gap-3 p-5">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Все референсы</h1>
          {data.data ? (
            <p className="mt-0.5 text-[12.5px] text-fg-muted tnum">
              показано {num(data.data.items.length)} из {num(total)}
            </p>
          ) : null}
        </div>
        {!searchOpen ? (
          <button type="button" className={`${btnPrimary} ml-auto`} onClick={() => setSearchOpen(true)}>
            <ScanSearch size={14} />
            Поиск
          </button>
        ) : null}
      </div>

      <LibNav current="all" />

      {searchOpen ? <PhotoSearchPanel onResult={setFound} onClose={() => setSearchOpen(false)} /> : null}

      {data.data?.truncated ? (
        <div className="rounded-[var(--radius-ui)] bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
          Показаны первые {num(data.data.max)} из {num(total)} — сузьте фильтр
        </div>
      ) : null}

      <div className="grid gap-2" data-annot="фильтры">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip on={!category} onClick={() => pickCategory("")}>
            все
          </Chip>
          {groups.map((g) => (
            <Chip key={g.code} on={category === g.code} onClick={() => pickCategory(g.code)}>
              {g.name} <span className="tnum text-fg-faint">{num(g.libs.reduce((n, l) => n + l.photos, 0))}</span>
            </Chip>
          ))}
          <select
            className={`${input} ml-1`}
            aria-label="Библиотека"
            value={libraryId ?? ""}
            onChange={(e) => patch({ library: e.target.value || null })}
          >
            <option value="">все библиотеки</option>
            {shownGroups.map((g) => (
              <optgroup key={g.code} label={g.name}>
                {g.libs.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <Chip on={tag === null} onClick={() => patch({ tag: null })}>
            все
          </Chip>
          <Chip on={tag === "none"} onClick={() => patch({ tag: "none" })}>
            без тега
          </Chip>
          {(tags ?? []).map((t) => (
            <Chip key={t.id} on={tag === t.id} onClick={() => patch({ tag: String(t.id) })}>
              {t.name} <span className="tnum text-fg-faint">{num(t.items)}</span>
            </Chip>
          ))}
          <button type="button" className={`${btnGhost} ml-auto`} onClick={() => setManaging(true)}>
            <Pencil size={12} />
            править теги
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Segmented<VerdictParam | "all">
            label="вердикт"
            value={verdict ?? "all"}
            options={[
              { value: "all", label: "все" },
              { value: "works", label: "работает" },
              { value: "fails", label: "не работает" },
              { value: "none", label: "без вердикта" },
            ]}
            onPick={(v) => patch({ verdict: v === "all" ? null : v })}
          />
          <input
            className={`${input} w-56`}
            type="search"
            placeholder="по заметке"
            aria-label="Поиск по заметке"
            maxLength={200}
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") writeQ(qText);
            }}
          />
          <Segmented<Sort>
            label="порядок"
            value={sort}
            options={[
              { value: "new", label: "новые" },
              { value: "old", label: "старые" },
              { value: "library", label: "по библиотеке" },
            ]}
            onPick={(s) => patch({ sort: s === "new" ? null : s })}
          />
        </div>
      </div>

      {found && scores ? (
        <ReferenceBrowser
          key="similar"
          items={found.view.items}
          mode="all"
          queryKey={found.queryKey}
          resetKey={JSON.stringify(found.queryKey)}
          scores={scores}
          percent={showsPercent(found.view)}
          title={
            <>
              {showsPercent(found.view) ? "Похожие" : "Найдено"} {found.label} <span className="tnum text-fg-faint">{num(found.view.items.length)}</span>
            </>
          }
          emptyText="Похожих не нашлось."
        />
      ) : data.isPending ? (
        <Loading />
      ) : data.isError && !data.data ? (
        <ErrorLine error={data.error} />
      ) : (
        <ReferenceBrowser
          key="all"
          items={items}
          mode="all"
          queryKey={libraryKeys.all(params)}
          resetKey={search.toString()}
          emptyText={
            filtered
              ? "Под фильтр не попал ни один референс"
              : "Референсов пока нет — положите пины из трендов или загрузите фото в библиотеку"
          }
        />
      )}

      <TagManager
        open={managing}
        onClose={() => setManaging(false)}
        onChanged={() => void queryClient.invalidateQueries({ queryKey: ["library"] })}
      />
    </div>
  );
}
